import { useQuery } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import useHttpClient from './useHttpClient';

export interface IFolder {
  'folderId':{
    'native': number;
    'partner'?: number;
  },
  'parentId'?:{
    'native': number;
    'partner'?: number;
  },
  'projectId':{
    'native': number;
    'partner'?: number;
  },
  'name':string;
  'links':{
    'project': string;
    'self': string;
    'children': string;
    'parent': string;
  }
}

interface IFolderList {
    'count': number;
    'offset': number;
    'limit': number;
    'hasMore': boolean;
    'requestedFields': string;
    'sortByName': boolean;
    'ascendingOrder': boolean;
    'items': IFolder[];
    'links': {
      'parent': string;
    }
}

export interface IFolderListPayload {
  called: boolean;
  loading: boolean;
  error?: Error;
  folderList?: IFolderList;
}
export interface IQueryFolderListHook {
  folderListResult: IFolderListPayload;
  getFolderList: (
    folderPath?: string,
    projectId?: number,
    sortByName?: boolean,
    ascendingOrder?: boolean
  ) => void;
}

interface IQueryParams {
  folderPath: string;
  projectId?: number;
  sortByName: boolean;
  ascendingOrder: boolean;
}

const useQueryFolderList = (): IQueryFolderListHook => {
  const httpClient = useHttpClient();
  const [queryParams, setQueryParams] = useState<IQueryParams | null>(null);

  const {
    data: folderList,
    error,
    isLoading: loading,
    isFetched,
  } = useQuery<IFolderList>({
    queryKey: ['folderList', queryParams?.folderPath, queryParams?.projectId, queryParams?.sortByName, queryParams?.ascendingOrder],
    queryFn: async () => {
      if (!queryParams) return undefined;

      const { folderPath, projectId, sortByName, ascendingOrder } = queryParams;
      const url = `${folderPath}?projectId=${projectId}&limit=1000&sortByName=${sortByName}&ascendingOrder=${ascendingOrder}`;

      const response = await httpClient.get(url);
      return response.data;
    },
    enabled: !!queryParams,
  });

  const getFolderList = useCallback(
    (folderPath: string = '/folders', projectId?: number, sortByName = true, ascendingOrder = true) => {
      setQueryParams({ folderPath, projectId, sortByName, ascendingOrder });
    }, []
  );

  return {
    folderListResult: {
      called: isFetched,
      error: error as Error | undefined,
      loading,
      folderList,
    },
    getFolderList,
  };
};

export default useQueryFolderList;
